<script setup>

import { ref, inject, computed, watch } from 'vue';
import BoutonMenuActions from './BoutonMenuActions.vue';
import TexteSaisieValeur from '@/components/generiques/TexteSaisieValeur.vue';
import PastilleValeur from '@/components/generiques/PastilleValeur.vue';
import {
	getElementsTableauLies,
	getPositionBriqueRapport } from '@/scripts/global.js';
import { trouverCelluleTableau } from '@/scripts/navigationRapport.js'
import { getAppelApiGlisserDeplacer } from '@/scripts/gestionGlisserDeplacer.js';
import { 
	apiModifierValeurs,
	apiModifierTextesBriquesRapport } from '@/services/ServiceModele.js';

const { tableau, element } = defineProps({
	tableau: Object,
	element: Object
});

const getChamp = inject('getChamp');
const getDimension = inject('getDimension');
const appelerApiModifModele = inject('appelerApiModifModele');
const selectionnerElement = inject('selectionnerElement');
const selectionnerPlageElements = inject('selectionnerPlageElements');
const elementsSelectionnes = inject('elementsSelectionnes');
const getElementParent = inject('getElementParent');
const basculerSelection = inject('basculerSelection');
const classeMasquage = inject('classeMasquage');
const donnerFocusAuRapport = inject('donnerFocusAuRapport');

const modeEdition = ref(false);
const texteSaisi = ref(element?.valeur?.valBrute);
const composantCellule = ref(null);
const cellulesLieesEnSurbrillance = ref([]);

const infobulle = computed(() => {
	if (element.valeur?.valInfo) {
		return `Valeur non formatée : ${element.valeur.valInfo}`;
	} else {
		return null;
	}
});

const typeDonnee = computed(() => {
	const coord = element.valeur?.coord;
	if (coord?.idChamp) {
		return getChamp(coord.idTypeObjet, coord.idChamp).typeDonnee;
	} else if (coord?.idDimension) {
		return getDimension(coord.idDimension).typeDonnee;
	}
});

watch(() => element, (elt) => {
	texteSaisi.value = elt?.valeur?.valBrute;
});

watch(() => element.selectionne, (sel) => {
	if (sel) {
		composantCellule.value.scrollIntoView({behaviour: "smooth", block: "nearest", inline: "nearest"});
		setTimeout(() => {
			if (element.selectionne) {
				activerSurbrillanceCellulesLiees();
			}
		}, 500);
	} else {
		desactiverSurbrillanceCellulesLiees();
	}
});

watch(() => element.modeEdition, (edition) => {
	if (edition) {
		delete element.modeEdition;
		activerModeEdition();
	}
});

watch(() => element.selectionElementParent, (sel) => {
	if (sel) {
		const elementEtParentSuperieur = getElementParent(element);
		if (elementEtParentSuperieur != null) {
			selectionnerElement(elementEtParentSuperieur.element, elementEtParentSuperieur.parent);
		}
		delete element.selectionElementParent;
	}
});

const stylePositionCellule = computed(() => {
	return {
		gridArea: `${element.ligne} / ${element.colonne} / ${element.ligne + element.hauteur} / ${element.colonne + element.largeur}`,
		paddingLeft: element.margeGauche != null ? `${element.margeGauche}px` : null,
		paddingTop: element.margeHaut != null ? `${element.margeHaut}px` : null,
		marginRight: '-1px',
		marginBottom: '-1px'
	};
})

const classesConteneurCellule = computed(() => {
	const classes = {};
	if (element.masque && !element.selectionne) {
		classes[classeMasquage.value] = true;
	}	
	return classes;
});

const classesCellule = computed(() => {
	return {
		selectionne: element.selectionne, 
		survol: element.survol, 
		'liee-a-selection': element.lieeASelection,
		'aucune-valeur': !element.valeur
	};
});

function activerModeEdition() {
	if (element?.valeur && 'valBrute' in element.valeur) {
		modeEdition.value = true;
	}
}

function desactiverModeEdition() {
	modeEdition.value = false;
	texteSaisi.value = element?.valeur?.valBrute;
	donnerFocusAuRapport();
}

function activerSurvol(element) {
	if (!element.selectionne) {
		element.survol = true;
	}
}

function desactiverSurvol(element) {
	if (element.survol) {
		delete element.survol;
	}
}

function activerSurbrillanceCellulesLiees() {
	cellulesLieesEnSurbrillance.value.length = 0;
	getElementsTableauLies(element, tableau).forEach(cellule => {
		cellulesLieesEnSurbrillance.value.push(cellule);
		cellule.lieeASelection = true;
	});
}

function desactiverSurbrillanceCellulesLiees() {
	cellulesLieesEnSurbrillance.value.forEach(cellule => {
		delete cellule.lieeASelection;
	});
	cellulesLieesEnSurbrillance.value.length = 0;
}

function commencerGlisserDeplacer(event) {
	var positionsBriquesDeplacees;
	if (element.selectionne) {
		positionsBriquesDeplacees = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent)).reverse();
	} else {
		positionsBriquesDeplacees = [getPositionBriqueRapport(element, tableau)];
	}
	const donneesTransfert = {
		type: 'DEPLACEMENT_BRIQUES',
		positionsBriquesDeplacees: positionsBriquesDeplacees
	};
	event.dataTransfer.setData('text/plain', JSON.stringify(donneesTransfert));
	event.dataTransfer.dropEffect = 'move';
}

function accueillirGlisserDeplacer(event) {
	const appelApi = getAppelApiGlisserDeplacer(event, element, tableau);
	if (appelApi) {
		appelerApiModifModele(appelApi.api, appelApi.argument, (reponseOK) => {
			if (reponseOK) {
				const coordCellule = {ligne: element.ligne, colonne: element.colonne};
				const cellule = trouverCelluleTableau(tableau, coordCellule);
				selectionnerElement(cellule, tableau);
			}
		});		
	}
}

function modifierTexte() {
	switch(element.type) {
		case 'ValeurExpression':
		case 'EnteteObjet':
			if (element.valeur.coord == null)
				break;
			const modifValeur = {
				coord: element.valeur.coord,
				valeur: texteSaisi.value
			};
			appelerApiModifModele(apiModifierValeurs, [modifValeur], (reponseOK) => {
				if (reponseOK) {
					desactiverModeEdition();
				}
			});
			break;
		case 'EnteteVariable':
		case 'EnteteRegroupement':
		case 'EnteteObjetsBloc':
		case 'EnteteExpression':
			const infosBriqueRapport = {
				positionBrique: getPositionBriqueRapport(element, tableau),
				texte: texteSaisi.value
			};
			appelerApiModifModele(apiModifierTextesBriquesRapport, [infosBriqueRapport], (reponseOK) => {
				if (reponseOK) {
					desactiverModeEdition();
				}
			});
			break;
	}
}

</script>

<template>
	<div class="conteneur-cellule" 
		ref="composantCellule" 
		:class="classesConteneurCellule" 
		:style="stylePositionCellule"
		:draggable="element.selectionne && !modeEdition"
		@dragstart.stop="commencerGlisserDeplacer"
		@dragenter.stop.prevent="activerSurvol(element)"
		@dragover.stop.prevent=""
		@dragleave.stop.prevent="desactiverSurvol(element)"
		@drop.stop.prevent="accueillirGlisserDeplacer">
		<div v-if="!modeEdition"
			class="cellule"
			:class="classesCellule"
			:style="element.style"
			v-tooltip="infobulle"
			@mousedown.exact.stop="selectionnerElement(element, tableau)"
			@mousedown.ctrl.exact.stop="basculerSelection(element, tableau)"
			@mousedown.shift.exact.stop="selectionnerPlageElements(element, tableau)"
			@dblclick="activerModeEdition()"
			@mouseover.stop="activerSurvol(element)"
			@mouseout.stop="desactiverSurvol(element)" >
				<i v-if="element.type === 'FiltreTableau'" class="fa-solid fa-filter icone-filtre"></i>
				{{ element.valeur?.valFormat }}
				<PastilleValeur v-if="element.valeur?.contexte" :valeur="element.valeur" />
		</div>
		<TexteSaisieValeur v-else
            class="cellule"
            v-model:texte="texteSaisi" 
			:typeDonnee="typeDonnee"
			:auto-focus="true"
			:style="element.style"
			@validation="modifierTexte()"
			@annulation="desactiverModeEdition()" />
		<BoutonMenuActions 
			v-if="!modeEdition && element.dernierElementSelectionne"
			:element="element" />
	</div>
</template>

<style scoped>

.conteneur-cellule {
	display: flex;
	scroll-margin: 10px;
	min-height: 2em;
}

.icone-filtre {
	color: rgb(64, 108, 221);
	margin-right: 5px;
}

</style>